import { useState } from 'react';
import { useInstructorCorrectiveActions, useCreateCorrectiveAction } from '@/api/instructor';
import { useAllTeamUsers } from '@/api/supervisor';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { EmptyState } from '@/components/shared/empty-state';
import { Combobox } from '@/components/shared/combobox';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/formatters';
import { Plus, Clock } from 'lucide-react';
import type { CorrectiveAction, CorrectiveActionStatus } from '@/types';

const STATUS_BADGES: Record<CorrectiveActionStatus, string> = {
  open: 'bg-red-100 text-red-700',
  in_progress: 'bg-amber-100 text-amber-700',
  resolved: 'bg-green-100 text-green-700',
};

const STATUS_LABELS: Record<CorrectiveActionStatus, string> = {
  open: 'Abierta',
  in_progress: 'En Progreso',
  resolved: 'Completada',
};

const TRIGGER_LABELS: Record<string, string> = {
  assessment_failed: 'Evaluación reprobada',
  quiz_failed: 'Quiz reprobado',
  manual: 'Manual',
  overdue_training: 'Capacitación vencida',
  quiz_failure: 'Quiz reprobado',
  assessment_failure: 'Evaluación reprobada',
};

function isOverdue(dueDate: string | null, status: CorrectiveActionStatus): boolean {
  if (!dueDate || status === 'resolved') return false;
  return new Date(dueDate).getTime() < Date.now();
}

export function InstructorCorrectiveActionsPage() {
  const { data: actions, isLoading, error } = useInstructorCorrectiveActions();
  const [showCreate, setShowCreate] = useState(false);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="No se pudieron cargar las acciones correctivas." />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Acciones Correctivas</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Gestiona las acciones correctivas de capacitación.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Crear
        </button>
      </div>

      {showCreate && <CreateForm onClose={() => setShowCreate(false)} />}

      {!actions?.length ? (
        <EmptyState
          title="Sin acciones correctivas"
          description="No hay acciones correctivas registradas."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {actions.map((action) => (
            <ActionCard key={action.id} action={action} />
          ))}
        </div>
      )}
    </div>
  );
}

function ActionCard({ action }: { action: CorrectiveAction }) {
  const overdue = isOverdue(action.due_date, action.status);

  return (
    <div
      className={cn(
        'rounded-xl border bg-card p-5',
        overdue ? 'border-red-300 bg-red-50/50 dark:bg-red-950/10' : 'border-border',
      )}
    >
      <p className="text-sm text-card-foreground">{action.description}</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
          {TRIGGER_LABELS[action.trigger_type] ?? action.trigger_type}
        </span>
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-xs font-medium',
            STATUS_BADGES[action.status],
          )}
        >
          {STATUS_LABELS[action.status]}
        </span>
        {overdue && (
          <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
            <Clock className="h-3 w-3" /> Vencida
          </span>
        )}
      </div>
      <div className="mt-3 space-y-0.5 text-xs text-muted-foreground">
        {action.created_at && <p>Creada: {formatDate(action.created_at)}</p>}
        {action.due_date && (
          <p className={cn(overdue && 'font-semibold text-red-600')}>
            Vence: {formatDate(action.due_date)}
          </p>
        )}
        {action.resolved_at && <p>Resuelta: {formatDate(action.resolved_at)}</p>}
      </div>
    </div>
  );
}

function CreateForm({ onClose }: { onClose: () => void }) {
  const create = useCreateCorrectiveAction();
  const { data: usersResponse } = useAllTeamUsers();
  const users = usersResponse?.data ?? [];
  const [userId, setUserId] = useState('');
  const [triggerType, setTriggerType] = useState('manual');
  const [description, setDescription] = useState('');
  const [requiredActions, setRequiredActions] = useState('');
  const [dueDate, setDueDate] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || !description || !requiredActions) return;

    create.mutate(
      {
        user_id: Number(userId),
        trigger_type: triggerType,
        description,
        required_actions: requiredActions,
        ...(dueDate && { due_date: dueDate }),
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 rounded-xl border border-border bg-card p-5">
      <h3 className="text-sm font-semibold text-card-foreground">Nueva Acción Correctiva</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="text-xs font-medium text-muted-foreground">Colaborador</label>
          <Combobox
            value={userId}
            onChange={setUserId}
            options={users.map((u) => ({
              value: String(u.id),
              label: `${u.first_name} ${u.last_name}`,
            }))}
            placeholder="Seleccionar colaborador..."
            clearable
          />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Tipo</label>
          <select
            value={triggerType}
            onChange={(e) => setTriggerType(e.target.value)}
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="manual">Manual</option>
            <option value="quiz_failure">Quiz reprobado</option>
            <option value="assessment_failure">Evaluación reprobada</option>
            <option value="overdue_training">Capacitación vencida</option>
          </select>
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground">Descripción</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          rows={2}
          className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground">Acciones Requeridas</label>
        <textarea
          value={requiredActions}
          onChange={(e) => setRequiredActions(e.target.value)}
          required
          rows={2}
          className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground">Fecha Limite (opcional)</label>
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={create.isPending}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {create.isPending ? 'Creando...' : 'Crear'}
        </button>
      </div>
    </form>
  );
}
